import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { enableScrolling, handleExternalLinkClick, updateLinksHref, updateLinksHrefNoLinksWarning } from 'lib/utils';
import Modal from 'components/helpers/Modal';
import { ExternalLinksWarningModalProps } from './ExternalLinkWarningTypes';
import { RichText } from '@sitecore-jss/sitecore-jss-nextjs';

/**
 * A Front-end only implementation of the Modal component.
 *
 * @example
 * ```tsx
 * <ExternalLinksWarningModal warningFields={warningFields} />
 * ```
 *
 * @param props - ExternalLinksWarningModalProps.
 * @returns A JSX element.
 */
export const ExternalLinksWarningModal: React.FC<ExternalLinksWarningModalProps> = (props) => {
  const { warningFields } = props;

  const [showModal, setShowModal] = useState(false);
  const [linkTarget, setLinkTarget] = useState('');
  const [navigationUrl, setNavigationUrl] = useState('');
  const router = useRouter();
  const addExternalLinksWarningModalDialog = warningFields && warningFields.text ? true : false;
  const whiteListDomains = warningFields && warningFields.whiteListDomains ? warningFields.whiteListDomains.value : '';
  const whiteListRoutes = warningFields && warningFields.whiteListRoutes ? warningFields.whiteListRoutes.value : '';

  const onExternalLinkClick = (url: string, target?: string | null): void => {
    setLinkTarget(target || '');
    enableScrolling(false);
    setNavigationUrl(url);
    setShowModal(true);
  };

  const OnProceedToLinkClick = useCallback((url: string, target?: string | null): void => {
    if (target === '_blank') {
      window.open(url);
    } else {
      window.location.href = url;
    }
  }, []);

  useEffect(() => {
    const handleLinkClick = (event: MouseEvent) => {
      handleExternalLinkClick(event, whiteListDomains, onExternalLinkClick, OnProceedToLinkClick, whiteListRoutes);
    };

    const updateLinksHrefRef = addExternalLinksWarningModalDialog ? updateLinksHref : updateLinksHrefNoLinksWarning;

    setTimeout(() => {
      updateLinksHrefRef();
    }, 1000);

    if (addExternalLinksWarningModalDialog) {
      document.addEventListener('click', handleLinkClick);
    }

    router.events.on('routeChangeComplete', updateLinksHrefRef);

    return () => {
      document.removeEventListener('click', handleLinkClick);
      router.events.off('routeChangeComplete', updateLinksHrefRef);
    };
  }, [
    router,
    setNavigationUrl,
    setShowModal,
    addExternalLinksWarningModalDialog,
    whiteListDomains,
    whiteListRoutes,
    OnProceedToLinkClick,
  ]);

  return addExternalLinksWarningModalDialog ? (
    <Modal
      opened={showModal}
      title={warningFields?.title?.value || ''}
      okButtonLabel={warningFields?.okButton?.value || ''}
      closeButtonLabel={warningFields?.cancelButton?.value || ''}
      showFooter={true}
      onOKCallback={() => {
        enableScrolling();
        OnProceedToLinkClick(navigationUrl, linkTarget);
      }}
      onCloseCallback={() => {
        enableScrolling();
        setShowModal(false);
      }}
    >
      <RichText field={{ value: warningFields?.text?.value || '' }} tag="div" />
    </Modal>
  ) : (
    <></>
  );
};

ExternalLinksWarningModal.displayName = 'ExternalLinksWarningModal';

export default ExternalLinksWarningModal;
